import { Resource, Location } from '@dispatch/shared/types';
import React from 'react';
import * as turf from '@turf/turf';
import { Feature, GeoJsonProperties, Polygon } from 'geojson';

export const useRadiusLayer = ({
  multiSelected,
}: {
  multiSelected?: Resource[];
}) => {
  const [overlappingCords, setOverlappingCords] = React.useState<
    Record<string, string[]>
  >({});

  const [circles, setCircles] = React.useState<
    Feature<Polygon, GeoJsonProperties>[]
  >([]);

  const handleDuplicateCoordinates = (
    duplicateCoords: Record<string, string[]>,
    destinationCoordinates: [number, number],
    resource: Resource
  ) => {
    if (
      !duplicateCoords[
        `${destinationCoordinates[0]}_${destinationCoordinates[1]}`
      ]
    ) {
      duplicateCoords[
        `${destinationCoordinates[0]}_${destinationCoordinates[1]}`
      ] = [resource.name];
    } else {
      duplicateCoords[
        `${destinationCoordinates[0]}_${destinationCoordinates[1]}`
      ] = Array.from(
        new Set([
          ...duplicateCoords[
            `${destinationCoordinates[0]}_${destinationCoordinates[1]}`
          ],
          resource.name,
        ])
      );
    }
    return duplicateCoords;
  };

  React.useEffect(() => {
    if (multiSelected && multiSelected?.length <= 0) return;
    // get duplicate coordinates to render multiple names in popup
    const duplicateCoords = overlappingCords;
    multiSelected?.forEach((resource: Resource) => {
      if (resource?.locations?.length > 0) {
        return resource?.locations?.forEach((location: Location) => {
          const radius = location.allowedTravelRadius || 1;
          const center: any = [location.long, location.lat];
          const circle = turf.circle(center, radius, {
            units: 'miles',
            steps: 50,
          } as any);

          // Calculate the destination point (bottom edge of the circle)
          const destinationPoint = turf.destination(center, radius, 180, {
            units: 'miles',
          } as any);

          const rightEdge = turf.destination(center, radius, 90, {
            units: 'miles',
          });

          const leftEdge = turf.destination(center, radius, 270, {
            units: 'miles',
          });

          // Extract the coordinates of the destination point
          const destinationCoordinates = destinationPoint.geometry
            .coordinates as [number, number];

          const rightEdgeCoords = rightEdge.geometry.coordinates;
          const leftEdgeCoords = leftEdge.geometry.coordinates;

          // handles adding duplicate coordinates
          const duplicates = handleDuplicateCoordinates(
            duplicateCoords,
            destinationCoordinates,
            resource
          );

          setOverlappingCords(duplicates);

          circle.properties = {
            name: resource.name,
            destinationCoordinates,
            rightEdgeCoords,
            leftEdgeCoords,
            coordinatesKey: `${destinationCoordinates[0]}_${destinationCoordinates[1]}`,
            isStartingLocation: location.isStartingLocation,
            center,
          };

          const circlesArray = circles;
          circles.push(circle);
          setCircles(circlesArray);
        });
      }
      return [];
    });
  }, [multiSelected]);

  React.useEffect(() => {
    if (multiSelected && multiSelected?.length <= 0) {
      setCircles([]);
    }
  }, [multiSelected]);

  return {
    overlappingCords,
    circles,
    setCircles,
  };
};
